import { AdminApiError, adminClient } from '@/api/clients/admin-client'
import { parseAdminBlobResponse } from '@/api/admin/base/crud'
import type { AdminBlobDownload } from '@/api/admin/base/types'
import type { AdminPageData, AdminPageQuery } from '@/shared/types/admin'
import type { ApiResponse } from '@/shared/types/http'

export type SystemId = string | number
export type SystemRecord = Record<string, unknown>
export type SystemFilters = Record<string, string | number>
export type SystemAreaNode = SystemRecord & { value: string; label: string; children?: SystemAreaNode[] }

export interface AdminSystemInfo {
  sysTime?: number
  osName?: string
  osVersion?: string
  osArch?: string
  processors?: number
  totalPhysical?: number
  freePhysical?: number
  memoryRate?: number
  userLanguage?: string
  jvmName?: string
  javaVersion?: string
  javaHome?: string
  userDir?: string
  javaTotalMemory?: number
  javaFreeMemory?: number
  javaMaxMemory?: number
  userName?: string
  systemCpuLoad?: number
  userTimezone?: string
}

export interface SystemResourceOptions {
  paged?: boolean
  deleteByPath?: boolean
  exportName?: string
}

const TOKEN_QUERY_KEY = /^token$/i

export const compactSystemQuery = <T extends object>(params: T): T => {
  if (Object.keys(params).some(key => TOKEN_QUERY_KEY.test(key))) {
    throw new Error('Admin token 禁止进入 URL 查询参数')
  }
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)) as T
}

const requireId = (id: SystemId): string => {
  const value = String(id).trim()
  if (!value || (typeof id === 'number' && !Number.isFinite(id))) throw new Error('系统记录 ID 不合法')
  return encodeURIComponent(value)
}

const parsePage = (data: unknown, label: string, paged: boolean, code: string | number): AdminPageData<SystemRecord> => {
  if (!paged) {
    if (!Array.isArray(data)) throw new AdminApiError(`${label}列表响应格式错误`, code)
    return { list: data as SystemRecord[], total: data.length }
  }
  const value = data as Partial<AdminPageData<SystemRecord>> | undefined
  if (!value || !Array.isArray(value.list) || typeof value.total !== 'number') throw new AdminApiError(`${label}分页响应格式错误`, code)
  return value as AdminPageData<SystemRecord>
}

export const createSystemResourceApi = (path: string, label: string, options: SystemResourceOptions = {}) => ({
  async page(query: AdminPageQuery<SystemFilters>): Promise<AdminPageData<SystemRecord>> {
    const response = await adminClient.get<ApiResponse<unknown>>(`${path}/${options.paged === false ? 'list' : 'page'}`, { params: compactSystemQuery(query) })
    return parsePage(response.data.data, label, options.paged !== false, response.data.code)
  },
  async list(filters: SystemFilters = {}): Promise<SystemRecord[]> {
    const response = await adminClient.get<ApiResponse<SystemRecord[]>>(`${path}/list`, { params: compactSystemQuery(filters) })
    if (!Array.isArray(response.data.data)) throw new AdminApiError(`${label}列表响应格式错误`, response.data.code)
    return response.data.data
  },
  async detail(id: SystemId): Promise<SystemRecord> {
    const response = await adminClient.get<ApiResponse<SystemRecord>>(`${path}/${requireId(id)}`)
    if (!response.data.data || typeof response.data.data !== 'object') throw new AdminApiError(`${label}详情响应格式错误`, response.data.code)
    return response.data.data
  },
  async create(payload: SystemRecord): Promise<void> { await adminClient.post<ApiResponse>(path, payload) },
  async update(payload: SystemRecord): Promise<void> { await adminClient.put<ApiResponse>(path, payload) },
  async remove(ids: readonly SystemId[]): Promise<void> {
    const targets = [...new Set(ids)].filter(id => String(id).trim())
    if (!targets.length) throw new Error('系统删除目标不能为空')
    if (options.deleteByPath) {
      for (const id of targets) await adminClient.delete<ApiResponse>(`${path}/${requireId(id)}`)
      return
    }
    await adminClient.delete<ApiResponse>(path, { data: targets })
  },
  async exportTable(filters: SystemFilters): Promise<AdminBlobDownload> {
    const response = await adminClient.get<Blob>(`${path}/export`, { params: compactSystemQuery(filters), responseType: 'blob' })
    return await parseAdminBlobResponse(response, options.exportName || `${label}.xlsx`)
  }
})

export const systemApis = {
  dept: createSystemResourceApi('/sys/dept', '部门', { paged: false, deleteByPath: true }),
  dictdata: createSystemResourceApi('/sys/dict/data', '字典数据'),
  dicttype: createSystemResourceApi('/sys/dict/type', '字典类型'),
  loglogin: createSystemResourceApi('/sys/log/login', '登录日志', { exportName: '登录日志.xlsx' }),
  logoperation: createSystemResourceApi('/sys/log/operation', '操作日志', { exportName: '操作日志.xlsx' }),
  logerror: createSystemResourceApi('/sys/log/error', '异常日志', { exportName: '异常日志.xlsx' }),
  menu: createSystemResourceApi('/sys/menu', '菜单', { paged: false, deleteByPath: true }),
  params: createSystemResourceApi('/sys/params', '参数'),
  post: createSystemResourceApi('/sys/post', '岗位'),
  role: createSystemResourceApi('/sys/role', '角色'),
  sysarea: createSystemResourceApi('/sys/sysarea', '业务区域', { exportName: '业务区域.xlsx' }),
  user: createSystemResourceApi('/sys/user', '管理员', { exportName: '管理员.xlsx' })
} as const

export const getSystemOptions = async (source: 'dept' | 'menu' | 'post' | 'role'): Promise<SystemRecord[]> => {
  const paths = { dept: '/sys/dept/list', menu: '/sys/menu/select', post: '/sys/post/list', role: '/sys/role/list' } as const
  const response = await adminClient.get<ApiResponse<SystemRecord[]>>(paths[source])
  if (!Array.isArray(response.data.data)) throw new AdminApiError(`${source}选项响应格式错误`, response.data.code)
  if (source === 'post') {
    return response.data.data.map(row => ({ ...row, name: row.postName }))
  }
  return response.data.data
}

export const updateSystemAreaOpen = async (id: SystemId, isOpen: 0 | 1): Promise<void> => {
  await adminClient.get<ApiResponse>(`/sys/sysarea/updateIsOpen/${requireId(id)}`, { params: { isOpen } })
}

export const buildSystemAreaTree = (rows: SystemRecord[]): SystemAreaNode[] => {
  const nodes = new Map<string, SystemAreaNode>()
  for (const row of rows) {
    const code = String(row.adcode || '').trim()
    if (!code || nodes.has(code)) continue
    nodes.set(code, { ...row, value: code, label: String(row.areaName || code) })
  }
  const roots: SystemAreaNode[] = []
  for (const node of nodes.values()) {
    const parentCode = String(node.parentCode || '0')
    const parent = nodes.get(parentCode)
    if (!parent || parent === node) roots.push(node)
    else (parent.children ||= []).push(node)
  }
  return roots
}

export const getSystemAreaTree = async (): Promise<SystemAreaNode[]> => {
  const response = await adminClient.get<ApiResponse<SystemRecord[]>>('/sys/sysarea/tree')
  if (!Array.isArray(response.data.data)) throw new AdminApiError('行政区划级联响应格式错误', response.data.code)
  return buildSystemAreaTree(response.data.data)
}

export const getAdminSystemInfo = async (): Promise<AdminSystemInfo> => {
  const response = await adminClient.get<ApiResponse<AdminSystemInfo>>('/sys/info')
  if (!response.data.data || typeof response.data.data !== 'object') {
    throw new AdminApiError('系统信息响应格式错误', response.data.code)
  }
  return response.data.data
}
